import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useSearchStore } from '@/store/searchStore';
import { useCollaborationStore } from '@/store/collaborationStore';
import { useCommentStore } from '@/store/commentStore';
import { getLayoutEngine } from '@/layout/layoutEngine';
import { measureTextSize } from '@/layout/measure';
import type { LayoutResult } from '@/layout/types';
import type { Point } from '@/utils/geometry';
import { NodeRenderer } from './NodeRenderer';
import { ConnectorRenderer } from './ConnectorRenderer';
import { CrossLinkRenderer } from './CrossLinkRenderer';
import { ConnectionGhost } from './ConnectionGhost';
import { InlineEditor } from './InlineEditor';
import { DragGhost } from './DragGhost';
import { useDragNode } from './hooks/useDragNode';
import { useNodeResize } from './hooks/useNodeResize';
import { useTouchCanvas } from './hooks/useTouchCanvas';
import { ContextMenu as ContextMenuComponent } from '@/ui/ContextMenu';
import { ImagePreviewModal } from '@/ui/ImagePreviewModal';
import { NodeToolbar } from '@/ui/NodeToolbar';

interface CanvasProps {
  mapId?: string | null;
}

export function Canvas({ mapId }: CanvasProps = {}) {
  const svgRef = useRef<SVGSVGElement>(null);

  const panX = useEditorStore((s) => s.panX);
  const panY = useEditorStore((s) => s.panY);
  const zoom = useEditorStore((s) => s.zoom);
  const setPan = useEditorStore((s) => s.setPan);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const editingNodeId = useEditorStore((s) => s.editingNodeId);
  const selectNode = useEditorStore((s) => s.selectNode);
  const clearSelection = useEditorStore((s) => s.clearSelection);
  const startEditing = useEditorStore((s) => s.startEditing);
  const multiSelectMode = useEditorStore((s) => s.multiSelectMode);

  const mode = useEditorStore((s) => s.mode);
  const connectionSourceId = useEditorStore((s) => s.connectionSourceId);
  const cancelConnecting = useEditorStore((s) => s.cancelConnecting);

  const nodes = useDocumentStore((s) => s.nodes);
  const connections = useDocumentStore((s) => s.connections);
  const meta = useDocumentStore((s) => s.meta);
  const settings = useDocumentStore((s) => s.settings);
  const revision = useDocumentStore((s) => s.revision);
  const mapDoc = useDocumentStore((s) => s.mapDoc);

  const searchMatchingIds = useSearchStore((s) => s.matchingNodeIds);

  const collaborators = useCollaborationStore((s) => s.collaborators);
  const allComments = useCommentStore((s) => s.comments);

  // Build comment count map: nodeId -> { total, unresolved }
  const commentCountsByNode = useMemo(() => {
    const result = new Map<string, { total: number; unresolved: number }>();
    allComments.forEach((comments, nodeId) => {
      const total = comments.length;
      const unresolved = comments.filter(
        (c) => !c.parentCommentId && !c.resolved,
      ).length;
      result.set(nodeId, { total, unresolved });
    });
    return result;
  }, [allComments]);

  // Build a map of nodeId -> list of collaborator selections for that node
  const collaboratorsByNode = useMemo(() => {
    const result = new Map<string, Array<{ color: string; name: string }>>();
    collaborators.forEach((c) => {
      if (!c.selectedNodeId) return;
      let arr = result.get(c.selectedNodeId);
      if (!arr) {
        arr = [];
        result.set(c.selectedNodeId, arr);
      }
      arr.push({ color: c.color, name: c.name });
    });
    return result;
  }, [collaborators]);

  // Track drag state for panning
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Marquee (rectangle) selection state -- coordinates are in SVG-local space
  const [marquee, setMarquee] = useState<{
    startX: number; startY: number;
    endX: number; endY: number;
    offsetX: number; offsetY: number;
  } | null>(null);

  // Mouse position in SVG coords for connection ghost
  const [mousePosSvg, setMousePosSvg] = useState<Point>({ x: 0, y: 0 });

  // Context menu state
  const setContextMenuOpen = useEditorStore((s) => s.setContextMenuOpen);
  const [contextMenu, setContextMenuRaw] = useState<{ x: number; y: number; nodeId: string } | null>(null);
  const setContextMenu = useCallback(
    (value: { x: number; y: number; nodeId: string } | null) => {
      setContextMenuRaw(value);
      setContextMenuOpen(value !== null);
    },
    [setContextMenuOpen],
  );
  const [imagePreview, setImagePreview] = useState<{ src: string; alt: string } | null>(null);

  const handleImagePreview = useCallback((src: string, alt: string) => {
    setImagePreview({ src, alt });
  }, []);

  // Compute layout
  const layout: LayoutResult = useMemo(() => {
    if (!meta.rootNodeId || nodes.size === 0) {
      return { nodeBounds: new Map(), connectorPaths: [], totalBounds: { x: 0, y: 0, width: 0, height: 0 } };
    }
    return getLayoutEngine(settings.layoutType).compute({
      nodes,
      rootNodeId: meta.rootNodeId,
      settings: {
        horizontalGap: settings.horizontalGap,
        verticalGap: settings.verticalGap,
        nodePaddingX: 16,
        nodePaddingY: 10,
      },
      measureText: measureTextSize,
    });
  }, [nodes, meta.rootNodeId, settings, revision]);

  // Drag-and-drop reparenting
  const {
    isDragging,
    dragNodeId,
    dropTargetId,
    ghostPosition,
    isFreePositioning,
    onNodeMouseDown,
    onCanvasMouseMove: onDragMouseMove,
    onCanvasMouseUp: onDragMouseUp,
    onNodeTouchStart,
    onCanvasTouchMove: onDragTouchMove,
    onCanvasTouchEnd: onDragTouchEnd,
  } = useDragNode(svgRef, layout, nodes, meta.rootNodeId, mapDoc, panX, panY, zoom);

  // Node resize
  const {
    resizingNodeId,
    resizePreviewSize,
    onResizeHandleMouseDown,
    onCanvasMouseMove: onResizeMouseMove,
    onCanvasMouseUp: onResizeMouseUp,
    onResizeHandleTouchStart,
    onCanvasTouchMove: onResizeTouchMove,
    onCanvasTouchEnd: onResizeTouchEnd,
  } = useNodeResize(svgRef, layout, mapDoc, panX, panY, zoom);

  // Pan handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only pan/marquee on direct SVG background click
      if (e.target === svgRef.current || (e.target as SVGElement).classList.contains('canvas-bg')) {
        if (multiSelectMode) {
          // Start marquee selection instead of panning
          const svgRect = svgRef.current?.getBoundingClientRect();
          const ox = svgRect?.left ?? 0;
          const oy = svgRect?.top ?? 0;
          setMarquee({
            startX: e.clientX - ox, startY: e.clientY - oy,
            endX: e.clientX - ox, endY: e.clientY - oy,
            offsetX: ox, offsetY: oy,
          });
        } else {
          // Normal pan behavior
          setIsPanning(true);
          panStart.current = { x: e.clientX, y: e.clientY, panX, panY };
          clearSelection();
        }
      }
    },
    [panX, panY, clearSelection, multiSelectMode],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Update marquee end position while dragging
      if (marquee) {
        setMarquee(prev => prev ? { ...prev, endX: e.clientX - prev.offsetX, endY: e.clientY - prev.offsetY } : null);
        return;
      }

      onResizeMouseMove(e);
      onDragMouseMove(e);

      // Track SVG-space mouse position for connection ghost
      if (mode === 'connecting') {
        const svgX = (e.clientX - panX) / zoom;
        const svgY = (e.clientY - panY) / zoom;
        setMousePosSvg({ x: svgX, y: svgY });
      }

      if (!isPanning) return;
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPan(panStart.current.panX + dx, panStart.current.panY + dy);
    },
    [isPanning, setPan, onDragMouseMove, onResizeMouseMove, mode, panX, panY, zoom, marquee],
  );

  const handleMouseUp = useCallback(() => {
    if (marquee) {
      // Marquee coordinates are already in SVG-local space
      const rect = {
        left: Math.min(marquee.startX, marquee.endX),
        right: Math.max(marquee.startX, marquee.endX),
        top: Math.min(marquee.startY, marquee.endY),
        bottom: Math.max(marquee.startY, marquee.endY),
      };

      // Find all nodes whose screen-space bounds intersect the marquee
      const selected: string[] = [];
      for (const [id, nodeBounds] of layout.nodeBounds) {
        // Convert node bounds to screen coordinates (relative to SVG element)
        const screenLeft = nodeBounds.x * zoom + panX;
        const screenTop = nodeBounds.y * zoom + panY;
        const screenRight = (nodeBounds.x + nodeBounds.width) * zoom + panX;
        const screenBottom = (nodeBounds.y + nodeBounds.height) * zoom + panY;

        // Check intersection
        if (screenRight >= rect.left && screenLeft <= rect.right &&
            screenBottom >= rect.top && screenTop <= rect.bottom) {
          selected.push(id);
        }
      }

      // Select all intersecting nodes
      if (selected.length > 0) {
        selectNode(selected[0]!);
        for (let i = 1; i < selected.length; i++) {
          selectNode(selected[i]!, true);
        }
      }

      setMarquee(null);
      return;
    }

    onResizeMouseUp();
    onDragMouseUp();
    setIsPanning(false);
  }, [onDragMouseUp, onResizeMouseUp, marquee, layout.nodeBounds, zoom, panX, panY, selectNode]);

  const handleMouseLeave = useCallback(() => {
    if (marquee) {
      // Cancel marquee selection when mouse leaves the canvas
      setMarquee(null);
      return;
    }
    // Fall through to normal mouse-up behavior (stop panning, finish drag/resize)
    onResizeMouseUp();
    onDragMouseUp();
    setIsPanning(false);
  }, [marquee, onDragMouseUp, onResizeMouseUp]);

  // Touch move/end handlers for drag and resize
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      onResizeTouchMove(e);
      onDragTouchMove(e);
    },
    [onDragTouchMove, onResizeTouchMove],
  );

  const handleTouchEnd = useCallback(() => {
    onResizeTouchEnd();
    onDragTouchEnd();
  }, [onDragTouchEnd, onResizeTouchEnd]);

  // Zoom handler -- must use native listener with { passive: false } to allow preventDefault.
  // React's synthetic onWheel is always passive in modern browsers.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      useEditorStore.getState().adjustZoom(delta);
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Touch context-menu handler (called from useTouchCanvas on long-press)
  const handleTouchContextMenu = useCallback(
    (x: number, y: number, nodeId: string) => {
      setContextMenu({ x, y, nodeId });
    },
    [],
  );

  // Touch double-tap handler (called from useTouchCanvas)
  const handleDoubleTapNode = useCallback(
    (nodeId: string) => {
      startEditing(nodeId);
    },
    [startEditing],
  );

  // Touch pan/zoom/long-press/double-tap support
  useTouchCanvas({
    svgRef,
    panX,
    panY,
    zoom,
    setPan,
    onContextMenu: handleTouchContextMenu,
    onDoubleTapNode: handleDoubleTapNode,
  });

  // Center on root on first render
  useEffect(() => {
    if (svgRef.current && meta.rootNodeId) {
      const rect = svgRef.current.getBoundingClientRect();
      setPan(rect.width / 2, rect.height / 2);
    }
    // Only on mount
  }, [meta.rootNodeId]);

  // Node interaction handlers
  const handleNodeClick = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setContextMenu(null);

      // In connecting mode, complete the connection
      if (mode === 'connecting' && connectionSourceId && connectionSourceId !== nodeId) {
        mapDoc.addConnection(connectionSourceId, nodeId);
        cancelConnecting();
        return;
      }

      const multi = multiSelectMode || e.ctrlKey || e.metaKey;
      selectNode(nodeId, multi);
    },
    [selectNode, multiSelectMode, mode, connectionSourceId, mapDoc, cancelConnecting],
  );

  const handleNodeDoubleClick = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      startEditing(nodeId);
    },
    [startEditing],
  );

  const handleNodeContextMenu = useCallback(
    (nodeId: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({ x: e.clientX, y: e.clientY, nodeId });
    },
    [],
  );

  // Arrow key navigation
  useEffect(() => {
    const handleNav = (e: KeyboardEvent) => {
      if (useEditorStore.getState().mode === 'editing') return;
      const selectedId = selectedNodeIds[0];
      if (!selectedId) return;

      const node = nodes.get(selectedId);
      if (!node) return;

      let targetId: string | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          targetId = node.parentId;
          break;
        case 'ArrowRight':
          if (node.childIds.length > 0 && !node.collapsed) {
            targetId = node.childIds[0] ?? null;
          }
          break;
        case 'ArrowUp':
        case 'ArrowDown': {
          if (!node.parentId) break;
          const parent = nodes.get(node.parentId);
          if (!parent) break;
          const idx = parent.childIds.indexOf(selectedId);
          const offset = e.key === 'ArrowUp' ? -1 : 1;
          const newIdx = idx + offset;
          if (newIdx >= 0 && newIdx < parent.childIds.length) {
            targetId = parent.childIds[newIdx] ?? null;
          }
          break;
        }
      }

      if (targetId) {
        e.preventDefault();
        selectNode(targetId);
      }
    };

    window.addEventListener('keydown', handleNav);
    return () => window.removeEventListener('keydown', handleNav);
  }, [selectedNodeIds, nodes, selectNode]);

  return (
    <>
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      style={{ background: 'var(--bg-canvas)', touchAction: 'none', cursor: isDragging ? (isFreePositioning ? 'move' : 'grabbing') : isPanning ? 'grabbing' : marquee ? 'crosshair' : multiSelectMode ? 'crosshair' : 'default' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <rect className="canvas-bg" width="100%" height="100%" fill="transparent" />
      <g transform={`translate(${panX}, ${panY}) scale(${zoom})`}>
        {/* Connectors */}
        <g className="connectors">
          {layout.connectorPaths.map((cp) => (
            <ConnectorRenderer key={`${cp.parentId}-${cp.childId}`} path={cp.path} />
          ))}
        </g>

        {/* Cross-links (non-hierarchical connections) */}
        <g className="cross-links">
          {Array.from(connections.values()).map((conn) => (
            <CrossLinkRenderer
              key={conn.id}
              connection={conn}
              nodeBounds={layout.nodeBounds}
            />
          ))}
        </g>

        {/* Connection ghost (during connection creation) */}
        {mode === 'connecting' && connectionSourceId && layout.nodeBounds.has(connectionSourceId) && (
          <ConnectionGhost
            sourceNodeBounds={layout.nodeBounds.get(connectionSourceId)!}
            mousePosition={mousePosSvg}
          />
        )}

        {/* Nodes */}
        <g className="nodes">
          {Array.from(layout.nodeBounds.values()).map((bounds) => {
            const node = nodes.get(bounds.id);
            if (!node) return null;
            return (
              <NodeRenderer
                key={bounds.id}
                node={node}
                bounds={bounds}
                isSelected={selectedNodeIds.includes(bounds.id)}
                isDropTarget={dropTargetId === bounds.id}
                isSearchMatch={searchMatchingIds.includes(bounds.id)}
                isResizing={resizingNodeId === bounds.id}
                resizePreviewSize={resizingNodeId === bounds.id ? resizePreviewSize : null}
                collaboratorSelections={collaboratorsByNode.get(bounds.id)}
                onClick={handleNodeClick}
                onDoubleClick={handleNodeDoubleClick}
                onMouseDown={onNodeMouseDown}
                onTouchStart={onNodeTouchStart}
                onContextMenu={handleNodeContextMenu}
                onResizeHandleMouseDown={onResizeHandleMouseDown}
                onResizeHandleTouchStart={onResizeHandleTouchStart}
                mapId={mapId}
                onImagePreview={handleImagePreview}
                commentCount={commentCountsByNode.get(bounds.id)}
              />
            );
          })}
        </g>

        {/* Inline editor */}
        {editingNodeId && layout.nodeBounds.has(editingNodeId) && (
          <InlineEditor
            key={editingNodeId}
            nodeId={editingNodeId}
            bounds={layout.nodeBounds.get(editingNodeId)!}
          />
        )}

        {/* Drag ghost */}
        {isDragging && ghostPosition && dragNodeId && (
          <DragGhost
            x={ghostPosition.x}
            y={ghostPosition.y}
            text={nodes.get(dragNodeId)?.text ?? ''}
          />
        )}
      </g>

      {/* Marquee selection rectangle (rendered in SVG-local screen space, outside the transform group) */}
      {marquee && (
        <rect
          x={Math.min(marquee.startX, marquee.endX)}
          y={Math.min(marquee.startY, marquee.endY)}
          width={Math.abs(marquee.endX - marquee.startX)}
          height={Math.abs(marquee.endY - marquee.startY)}
          fill="rgba(59, 130, 246, 0.1)"
          stroke="var(--color-primary)"
          strokeWidth={1}
          strokeDasharray="4 2"
          pointerEvents="none"
        />
      )}
    </svg>

    {/* Floating node toolbar (HTML overlay, positioned above selected node) */}
    {selectedNodeIds[0] && layout.nodeBounds.has(selectedNodeIds[0]) && (
      <NodeToolbar
        bounds={layout.nodeBounds.get(selectedNodeIds[0])!}
        panX={panX}
        panY={panY}
        zoom={zoom}
      />
    )}

    {/* Context menu (HTML overlay, rendered outside SVG) */}
    {contextMenu && (
      <ContextMenuComponent
        x={contextMenu.x}
        y={contextMenu.y}
        nodeId={contextMenu.nodeId}
        onClose={() => setContextMenu(null)}
      />
    )}

    {/* Image preview modal */}
    {imagePreview && (
      <ImagePreviewModal
        src={imagePreview.src}
        alt={imagePreview.alt}
        onClose={() => setImagePreview(null)}
      />
    )}
    </>
  );
}
